import WarehouseNameTooltip from '../../components/WarehouseNameTooltip';
import RowActionsMenu from '../../components/RowActionsMenu';
import usePageViewState from '../../components/usePageViewState';
import { usePermissions } from '../../auth/permissions';
import Toast from '../../components/Toast';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Check, ChevronLeft, ChevronRight, ListChecks, MapPin, Pencil, Plus, RotateCcw, Search, Trash2, Warehouse } from 'lucide-react';
import FilterMenu from '../../components/FilterMenu';
import TableScroll from '../../components/TableScroll';
import { createWarehouse, deleteWarehouse, listCountries, listWarehouses, updateWarehouse } from './warehouseService';
import WarehouseModal from './WarehouseModal';
import './warehouses.css';
import usePersistentFilters, { validateFilters } from '../../components/usePersistentFilters';

const PAGE_SIZE = 6;

const emptyFilters = { query: '', country: '', capacity: '' };
const capacityOptions = [
  ['', 'All Capacities'],
  ['under-10000', 'Under 10,000 m³'],
  ['10000-30000', '10,000 – 30,000 m³'],
  ['over-30000', 'Over 30,000 m³'],
];

function formatCapacity(value, unit = 'm³') {
  return `${value.toLocaleString('en-US')} ${unit}`;
}

function matchesCapacity(value, filter) {
  if (!filter) return true;
  if (filter === 'under-10000') return value < 10000;
  if (filter === '10000-30000') return value >= 10000 && value < 30000;
  if (filter === 'over-30000') return value >= 30000;
  return true;
}

function capacityFilterLabel(value) {
  return capacityOptions.find(([key]) => key === value)?.[1] || 'All Capacities';
}



export default function WarehousesPage() {
  const navigate = useNavigate();
  const { canWrite } = usePermissions();
  const writable = canWrite('Warehouse');
  const [records, setRecords] = useState([]);
  const [countries, setCountries] = useState([]);
  const [filters, setFilters, reconcileFilters] = usePersistentFilters('warehousesFilters', emptyFilters);
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const [page, setPage] = useState(1);
  const [openMenu, setOpenMenu] = useState(null);
  const [warehouseModal, setWarehouseModal] = usePageViewState('warehouseModal', null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  const warehouses = useMemo(() => records.filter(item => !item.is_deleted), [records]);
  useEffect(() => {
    if (optionsLoaded) reconcileFilters(values => validateFilters(values, emptyFilters, {
      country: countries.map(country => country.id), capacity: capacityOptions.map(([key]) => key),
    }));
  }, [optionsLoaded, countries, reconcileFilters]);
  const countryOptions = useMemo(() => {
    return [{ id: '', name: 'All Locations' }, ...countries];
  }, [countries]);
  const selectedCountry = countries.find(country => String(country.id) === String(filters.country));
  const filtered = useMemo(() => {
    const query = filters.query.trim().toLowerCase();
    return warehouses.filter(item => {
      const matchesQuery = !query || [item.code || '', item.name, item.address, item.city, item.country].some(value => value.toLowerCase().includes(query));
      return matchesQuery && (!filters.country || String(item.country_id) === String(filters.country)) && matchesCapacity(item.capacity, filters.capacity);
    });
  }, [filters, warehouses]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const start = filtered.length ? (currentPage - 1) * PAGE_SIZE + 1 : 0;
  const end = Math.min(currentPage * PAGE_SIZE, filtered.length);
  const rows = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  useEffect(() => {
    let cancelled = false;
    async function loadWarehouses() {
      try {
        const items = await listWarehouses();
        if (!cancelled) { setRecords(items); }
      } catch (error) {
        if (!cancelled) {
          setRecords([]);
          showToast(error.message, 'error');
        }
      }
    }
    loadWarehouses();
    listCountries().then(items => {
      if (!cancelled) { setCountries(items); setOptionsLoaded(true); }
    }).catch(error => { if (!cancelled) showToast(error.message, 'error'); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {

  }, [filters, currentPage]);

  useEffect(() => {
    if (!deleteItem) return undefined;
    function handleKey(event) {
      if (event.key === 'Escape' && !deleting) closeDelete();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [deleteItem, deleting]);


  useEffect(() => () => {
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
  }, []);

  function updateFilter(name, value) {
    setFilters(current => ({ ...current, [name]: value }));
    setPage(1);

  }

  function closeMenus() {
    setOpenMenu(null);

  }

  function showToast(message, tone = 'success', title) {
    setToast({ message, tone, title });
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 4000);
  }

  function openAdd() {
    if (!writable) return;

    setWarehouseModal({ mode: 'add', warehouse: null });
  }

  function openEdit(selected) {
    if (!writable) return;

    if (selected) setWarehouseModal({ mode: 'edit', warehouse: selected });
  }

  function openDelete(selected) {
    if (!writable) return;

    if (selected) setDeleteItem(selected);
  }

  function closeWarehouseModal() {
    setWarehouseModal(null);
  }

  function closeDelete() {
    if (deleting) return;
    setDeleteItem(null);
  }

  async function saveWarehouse(payload) {
    if (!writable) return;
    if (warehouseModal?.mode === 'edit' && warehouseModal.warehouse) {
      const id = warehouseModal.warehouse.id;
      let saved;
      try {
        saved = await updateWarehouse(id, payload);
      } catch (error) {
        if (error.status === 403 || error.status === 404) {
          setWarehouseModal(null);
          showToast("You don't have permission to edit this warehouse.", 'error');
          return;
        }
        throw error;
      }
      setRecords(current => current.map(item => (
        item.id === id ? { ...item, ...payload, ...saved, id, capacityUnit: payload.capacityUnit, is_deleted: false } : item
      )));
      showToast('Warehouse updated.');
      return;
    }

    const saved = await createWarehouse(payload);
    if (!saved?.id) throw new Error('Warehouse was not saved to the database.');
    setRecords(current => [...current, {
      ...payload,
      ...saved,
      id: saved.id,
      capacityUnit: payload.capacityUnit || 'm³',
      is_deleted: false,
    }]);
    setPage(Number.MAX_SAFE_INTEGER);
    showToast('Warehouse created.');
  }

  async function confirmDelete() {
    if (!writable) return;
    if (deleting || !deleteItem) return;
    setDeleting(true);
    try {
      await deleteWarehouse(deleteItem.id);
      setRecords(current => current.map(item => (
        item.id === deleteItem.id ? { ...item, is_deleted: true } : item
      )));
      setDeleteItem(null);
      showToast('Warehouse deleted.');
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setDeleteItem(null);
      setDeleting(false);
    }
  }

  return <section className="wh-page wh-list-page warehouses-page">
    <div className="wh-title">
      <div>
        <h1>Warehouses</h1>
      </div>
      <div className="wh-title-actions">
        <button className="wh-add" type="button" disabled={!writable} onClick={openAdd}><Plus size={14} />Add Warehouse</button>
      </div>
    </div>
    <div className="wh-list">
      <div className="wh-panel">
      <div className="wh-filters">
        <label className="wh-search"><Search size={22} /><input type="search" aria-label="Search warehouses" placeholder="Search warehouses..." value={filters.query} onChange={event => updateFilter('query', event.target.value)} /></label>
        <FilterMenu
          className="country-filter"
          open={openMenu === 'country'}
          onToggle={() => { setOpenMenu(openMenu === 'country' ? null : 'country'); }}
          onClose={closeMenus}
          icon={<MapPin size={14} strokeWidth={1.8} />}
          label="Country"
          value={selectedCountry?.name || 'All Locations'}
          searchable
        >
          {countryOptions.map(country => {
            const value = country.id;
            const selected = String(filters.country) === String(value);
            return <button key={country.id || 'all'} className={`wh-menu-item${selected ? ' selected' : ''}`} type="button" role="option" aria-selected={selected} onClick={() => { updateFilter('country', value); closeMenus(); }}>
              <span>{country.name}</span>
              {selected && <Check size={14} strokeWidth={2} />}
            </button>;
          })}
        </FilterMenu>
        <FilterMenu
          open={openMenu === 'capacity'}
          onToggle={() => { setOpenMenu(openMenu === 'capacity' ? null : 'capacity'); }}
          onClose={closeMenus}
          icon={<Box size={14} strokeWidth={1.8} />}
          label="Capacity"
          value={capacityFilterLabel(filters.capacity)}
        >
          <div className="wh-menu-list">
            {capacityOptions.map(([value, label]) => {
              const selected = filters.capacity === value;
              return <button key={label} className={`wh-menu-item${selected ? ' selected' : ''}`} type="button" role="option" aria-selected={selected} onClick={() => { updateFilter('capacity', value); closeMenus(); }}>
                <span>{label}</span>
                {selected && <Check size={14} strokeWidth={2} />}
              </button>;
            })}
          </div>
        </FilterMenu>
        <button className="wh-filter-btn" type="button" onClick={() => { setFilters(emptyFilters); setPage(1); closeMenus(); }}><RotateCcw size={14} strokeWidth={1.7} />Reset</button>
      </div>
      <TableScroll>
        <table className="wh-table">
          <thead>
            <tr>
              <th className="wh-actions-heading" aria-label="Actions" title="Actions"><ListChecks size={16} aria-hidden="true" /></th>
              <th>Warehouse Code</th>
              <th>Warehouse</th>
              <th>Address</th>
              <th>City</th>
              <th>Country</th>
              <th>Capacity</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(item => <tr key={item.id} onClick={() => navigate(`/inventory?warehouseId=${encodeURIComponent(item.id)}`, { state: { fromWarehouses: true } })} style={{ cursor: 'pointer' }}>
              <td className="wh-actions-cell" onClick={event => event.stopPropagation()}>
                <RowActionsMenu label={`Actions for ${item.name}`} actions={[
                  { label: 'Edit', icon: Pencil, disabled: !writable, onClick: () => openEdit(item) },
                  { label: 'Delete', icon: Trash2, danger: true, disabled: !writable, onClick: () => openDelete(item) },
                ]} />
              </td>
              <td>{item.code || '?'}</td>
              <td><WarehouseNameTooltip name={item.name}><span className="wh-name" role="link" tabIndex={0} onKeyDown={event => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  navigate(`/inventory?warehouseId=${encodeURIComponent(item.id)}`, { state: { fromWarehouses: true } });
                }
              }}><Warehouse size={15} strokeWidth={1.7} />{item.name}</span></WarehouseNameTooltip></td>
              <td>{item.address}</td>
              <td>{item.city}</td>
              <td>{item.country}</td>
              <td>{formatCapacity(item.capacity, item.capacityUnit)}</td>
            </tr>)}
            {!rows.length && <tr><td className="wh-empty" colSpan={7}>{warehouses.length ? 'No warehouses match the current filters.' : 'No warehouses found.'}</td></tr>}
          </tbody>
        </table>
      </TableScroll>
      <div className="wh-pager">
        <p>Showing {start}–{end} of {filtered.length} Warehouses</p>
        <div>
          <button type="button" disabled={currentPage === 1} onClick={() => { setPage(currentPage - 1); }}><ChevronLeft size={14} />Previous</button>
          {Array.from({ length: pageCount }, (_, index) => <button key={index + 1} type="button" className={`wh-page-number${currentPage === index + 1 ? ' current' : ''}`} aria-current={currentPage === index + 1 ? 'page' : undefined} onClick={() => { setPage(index + 1); }}>{index + 1}</button>)}
          <button type="button" disabled={currentPage === pageCount} onClick={() => { setPage(currentPage + 1); }}>Next<ChevronRight size={14} /></button>
        </div>
      </div>
      </div>
    </div>


    {writable && warehouseModal && <WarehouseModal
      mode={warehouseModal.mode}
      warehouse={warehouseModal.warehouse}
      countries={countries}
      onClose={closeWarehouseModal}
      onSubmit={saveWarehouse}
    />}

    {writable && deleteItem && <div className="wh-modal-backdrop" onMouseDown={event => { if (event.target === event.currentTarget) closeDelete(); }}>
      <div className="wh-modal wh-modal-confirm" role="dialog" aria-modal="true" aria-labelledby="wh-delete-title">
        <h2 id="wh-delete-title">Delete Warehouse?</h2>
        <p className="wh-modal-copy">Are you sure you want to delete this warehouse?</p>
        <div className="wh-delete-target">
          <strong>{deleteItem.name}</strong>
          <span>{deleteItem.address}</span>
        </div>
        <div className="wh-modal-actions">
          <button className="wh-btn-ghost" type="button" onClick={closeDelete} disabled={deleting} autoFocus>Cancel</button>
          <button className="wh-btn-danger" type="button" onClick={confirmDelete} disabled={deleting}>{deleting ? 'Deleting...' : 'Delete'}</button>
        </div>
      </div>
    </div>}

    <Toast toast={toast} onClose={() => setToast(null)} />
  </section>;
}
